import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCaseDetailsWithReviews } from '@lp/features/RoutePlanner/hooks/useCaseDetailsWithReviews';
import { useHubResources } from '@lp/features/RoutePlanner/hooks/useHubResources';
import { RouteHub } from '@lp/features/RoutePlanner/types';
import { DonorType, OrganType } from '@/api/apis/case';
import { SelectItemType } from '@/types';
import { useLocationsDistance } from '@/queries/mission-planner-distance';
import { LocationPointType } from '@/api/apis/mission-planner-distance';
import { useHubsQuery } from '@/queries/hub';

type UseHubResourcesListProps = {
  defaultHubId?: string;
  isClosestHubCalculate?: boolean;
  fetchResources?: boolean;
};

type UseHubResourcesListType = {
  searchValue: string;
  setSearchValue: (searchValue: string) => void;
  isLoading: boolean;
  hubs: { id: string; hub: RouteHub; travelTime?: number }[];
  hubSelectList: SelectItemType[];
  onHubSelectChange: (hubId: string) => void;
  selectedHub?: RouteHub;
  organType?: OrganType;
};

export const useHubResourcesList = ({
  defaultHubId,
  isClosestHubCalculate = true,
  fetchResources = true,
}: UseHubResourcesListProps = {}): UseHubResourcesListType => {
  const {
    donorType,
    donorHospitalDetails: donorHospital,
    txCenterDetails: txCenter,
    organType,
  } = useCaseDetailsWithReviews();

  const origin = useMemo(() => {
    if (!isClosestHubCalculate) return null;

    if (donorType === DonorType.DBD && txCenter) {
      return {
        id: txCenter.id,
        location: {
          latitude: txCenter.location.latitude,
          longitude: txCenter.location.longitude,
        },
      };
    }

    if (donorHospital) {
      return {
        id: donorHospital.id,
        location: {
          latitude: donorHospital.location.latitude,
          longitude: donorHospital.location.longitude,
        },
      };
    }

    return null;
  }, [isClosestHubCalculate, donorHospital, txCenter, donorType]);

  const [searchValue, setSearchValue] = useState('');
  const [destinations, setDestinations] = useState<LocationPointType[]>([]);
  const [selectedHubId, setSelectedHubId] = useState(defaultHubId || '');
  const { isLoading: isResourcesLoading, getCountedResources } = useHubResources({ enabled: fetchResources });
  const { data: hubsList } = useHubsQuery();
  const { data: groundTravelTimes, isLoading: isTravelTimesLoaded } = useLocationsDistance({ origin, destinations });

  useEffect(() => {
    if (hubsList?.items.length && isClosestHubCalculate) {
      setDestinations(
        hubsList.items.map((h) => ({
          id: h.id,
          location: {
            latitude: h.location.latitude,
            longitude: h.location.longitude,
          },
        })),
      );
    }
  }, [isClosestHubCalculate, hubsList]);

  const hubs = useMemo(() => {
    if (isResourcesLoading) return [];

    const list =
      hubsList?.items.map((h) => {
        const countedResources = getCountedResources(h);

        const hub = {
          ...h,
          resources: countedResources,
        } as RouteHub;

        return {
          id: h.id,
          hub,
        };
      }) || [];

    if (groundTravelTimes && donorHospital && list.length) {
      return list
        .map((h) => ({
          ...h,
          travelTime: groundTravelTimes[h.id] || 0,
        }))
        .sort((a, b) => {
          if (a.travelTime <= 1) return 1;
          if (b.travelTime <= 1) return -1;

          return a.travelTime > b.travelTime ? 1 : -1;
        });
    }

    return list;
  }, [groundTravelTimes, donorHospital, hubsList, getCountedResources, isResourcesLoading]);

  const getDefaultHub = useCallback(
    (travelTimes: Record<string, number>) => {
      if (donorType === DonorType.DBD && donorHospital) {
        const currentHub = hubs.find((h) => travelTimes[h.id] <= 3 * 60 * 60 * 1000);

        if (currentHub) {
          setSelectedHubId(currentHub.id);
          return;
        }

        const currentSuperHub = hubs.find((h) => {
          return h.hub.isSuperHub;
        });

        if (currentSuperHub) {
          setSelectedHubId(currentSuperHub.id);
          return;
        }
      }

      if (donorType === DonorType.DCD) {
        setSelectedHubId(hubs[0].id);
      }
    },
    [donorType, donorHospital, hubs],
  );

  useEffect(() => {
    if (hubs.length && groundTravelTimes && !selectedHubId) {
      getDefaultHub(groundTravelTimes);
    }
  }, [hubs, groundTravelTimes, getDefaultHub, selectedHubId]);

  const hubSelectList = useMemo(
    () =>
      (hubs || []).map((item) => ({
        value: item.id,
        name: item.hub.name,
      })),
    [hubs],
  );

  const onHubSelectChange = useCallback((hubId: string) => {
    setSelectedHubId(hubId);
  }, []);

  const selectedHub = useMemo(() => {
    const hub = (hubs || []).find((item) => item.id === selectedHubId)?.hub;

    if (!hub) {
      return undefined;
    }

    return hub;
  }, [hubs, selectedHubId]);

  const hubsSearchList = useMemo(() => {
    if (searchValue) {
      return hubs.filter(
        (h) =>
          h.hub.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          h.hub.code.toLowerCase().includes(searchValue.toLowerCase()),
      );
    }

    return hubs;
  }, [hubs, searchValue]);

  return {
    searchValue,
    setSearchValue,
    isLoading: isResourcesLoading || isTravelTimesLoaded,
    hubs: hubsSearchList,
    hubSelectList,
    onHubSelectChange,
    selectedHub,
    organType,
  };
};
